import { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';

interface SiteContent {
  hero_title_line1?: string;
  hero_title_line2?: string;
  hero_subtitle?: string;
  hero_title_size?: string;
  hero_subtitle_size?: string;
  hero_title_font?: string;
  hero_subtitle_font?: string;
  hero_button1_text?: string;
  hero_button2_text?: string;
  hero_reel_button_enabled?: string;
  hero_reel_button_text?: string;
  hero_video_url?: string;
  hero_modal_video_url?: string;
  hero_backup_image_url?: string;
  hero_title_spacing?: string;
  hero_subtitle_spacing?: string;
  hero_button_spacing?: string;
  ticker_text?: string;
  ticker_speed?: string;
  ticker_font_size?: string;
  ticker_font_family?: string;
  ticker_height?: string;
  ticker_button_enabled?: string;
  ticker_button_text?: string;
}

interface CustomFont {
  font_family: string;
  font_display_name: string;
  woff2_url?: string;
  woff_url?: string;
  ttf_url?: string;
  otf_url?: string;
}

export default function HeroSection() {
  const [content, setContent] = useState<SiteContent>({});
  const [customFonts, setCustomFonts] = useState<CustomFont[]>([]);
  const [loading, setLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [fontsLoaded, setFontsLoaded] = useState(false);

  // Default content as fallback
  const defaultContent: SiteContent = {
    hero_title_line1: 'Creative',
    hero_title_line2: 'Roel & Wietse',
    hero_subtitle:
      'Crafting compelling visual narratives that connect brands with their audiences through innovative design and strategic storytelling.',
    hero_title_size: 'text-6xl md:text-8xl lg:text-9xl',
    hero_subtitle_size: 'text-lg md:text-xl lg:text-2xl',
    hero_title_font: 'Archivo',
    hero_subtitle_font: 'Archivo',
    hero_button1_text: 'View Our Work',
    hero_button2_text: 'Get In Touch',
    hero_reel_button_enabled: '1',
    hero_reel_button_text: 'View Our Reel',
    hero_video_url:
      'https://player.vimeo.com/video/1027209064?badge=0&autopause=0&player_id=0&app_id=58479',
    hero_modal_video_url: '',
    hero_backup_image_url: '',
    hero_title_spacing: 'mb-6',
    hero_subtitle_spacing: 'mb-4',
    hero_button_spacing: 'mt-8',
    ticker_text: 'CREATIVE WORK • DESIGN EXCELLENCE • VISUAL STORYTELLING • BRAND INNOVATION •',
    ticker_speed: '30',
    ticker_font_size: 'text-6xl md:text-8xl',
    ticker_font_family: 'Archivo',
    ticker_height: 'py-8',
    ticker_button_enabled: '0',
    ticker_button_text: 'Watch Our Story',
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch('/admin/content-api.php');
        const data = await response.json();

        if (data.success && data.data) {
          // Force the subtitle spacing to mb-4 and filter out invalid fonts
          const updatedContent = {
            ...defaultContent,
            ...data.data,
            hero_subtitle_spacing: 'mb-4',
            // Filter out invalid fonts that cause CORS errors
            hero_title_font: isValidFont(data.data.hero_title_font) ? data.data.hero_title_font : 'Inter',
            hero_subtitle_font: isValidFont(data.data.hero_subtitle_font) ? data.data.hero_subtitle_font : 'Inter',
            ticker_font_family: isValidFont(data.data.ticker_font_family) ? data.data.ticker_font_family : 'Inter',
          };
          setContent(updatedContent);

          // Fetch custom fonts if available
          if (data.custom_fonts) {
            setCustomFonts(data.custom_fonts);
          }
        } else {
          setContent(defaultContent);
        }
      } catch (error) {
        console.error('Error fetching content:', error);
        setContent(defaultContent);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  // Function to validate if a font exists and is safe to load
  const isValidFont = (fontName: string | undefined) => {
    if (!fontName || fontName === 'Inter') return true;

    // Allow all fonts - removed the problematic fonts filter
    // Custom fonts should be handled by the custom font loading system
    return true;
  };

  // Enhanced font loading with Safari compatibility
  useEffect(() => {
    const loadFonts = async () => {
      try {
        // Load custom fonts first
        if (customFonts.length > 0) {
          const style = document.createElement('style');
          let cssRules = '';

          customFonts.forEach(font => {
            const sources = [];
            if (font.woff2_url) sources.push(`url('${font.woff2_url}') format('woff2')`);
            if (font.woff_url) sources.push(`url('${font.woff_url}') format('woff')`);
            if (font.ttf_url) sources.push(`url('${font.ttf_url}') format('truetype')`);
            if (font.otf_url) sources.push(`url('${font.otf_url}') format('opentype')`);

            if (sources.length > 0) {
              cssRules += `
                @font-face {
                  font-family: '${font.font_family}';
                  src: ${sources.join(', ')};
                  font-display: swap;
                  font-weight: normal;
                  font-style: normal;
                }
              `;
            }
          });

          if (cssRules) {
            style.textContent = cssRules;
            document.head.appendChild(style);
            console.log('Custom fonts loaded:', customFonts.map(f => f.font_family));
          }
        }

        // Collect all Google Fonts needed
        const googleFontsToLoad = new Set<string>();

        // Check hero fonts
        if (content.hero_title_font && content.hero_title_font !== 'Inter' && !customFonts.find(f => f.font_family === content.hero_title_font)) {
          googleFontsToLoad.add(content.hero_title_font);
        }

        if (content.hero_subtitle_font && content.hero_subtitle_font !== 'Inter' && !customFonts.find(f => f.font_family === content.hero_subtitle_font)) {
          googleFontsToLoad.add(content.hero_subtitle_font);
        }

        if (content.ticker_font_family && content.ticker_font_family !== 'Inter' && !customFonts.find(f => f.font_family === content.ticker_font_family)) {
          googleFontsToLoad.add(content.ticker_font_family);
        }

        // Enhanced Google Font loading with Safari compatibility
        const loadGoogleFont = (fontName: string) => {
          return new Promise<void>((resolve) => {
            const fontNameForUrl = fontName.replace(/\s+/g, '+');
            const existingLink = document.querySelector(`link[href*="${fontNameForUrl}"]`);

            if (!existingLink) {
              const link = document.createElement('link');
              link.href = `https://fonts.googleapis.com/css2?family=${fontNameForUrl}:wght@100;200;300;400;500;600;700;800;900&display=swap`;
              link.rel = 'stylesheet';
              link.crossOrigin = 'anonymous';

              // Safari-specific loading handling
              let resolved = false;

              link.onload = () => {
                if (!resolved) {
                  resolved = true;
                  console.log(`Google Font loaded successfully: ${fontName}`);
                  resolve();
                }
              };

              link.onerror = () => {
                if (!resolved) {
                  resolved = true;
                  console.warn(`Failed to load Google Font: ${fontName}`);
                  resolve();
                }
              };

              document.head.appendChild(link);

              // Safari fallback timeout
              setTimeout(() => {
                if (!resolved) {
                  resolved = true;
                  console.warn(`Font loading timeout for: ${fontName}`);
                  resolve();
                }
              }, 4000);
            } else {
              resolve();
            }
          });
        };

        // Load all Google Fonts
        if (googleFontsToLoad.size > 0) {
          const fontPromises = Array.from(googleFontsToLoad).map(fontName => loadGoogleFont(fontName));
          await Promise.all(fontPromises);

          // Enhanced Safari font loading check
          if ('fonts' in document) {
            try {
              // Wait for document fonts to be ready
              await document.fonts.ready;

              // Additional Safari-specific font loading verification
              const fontChecks = Array.from(googleFontsToLoad).map(async (fontName) => {
                try {
                  await document.fonts.load(`16px "${fontName}"`);
                  console.log(`Font face verified: ${fontName}`);
                } catch (error) {
                  console.warn(`Font face verification failed for ${fontName}:`, error);
                }
              });

              await Promise.all(fontChecks);
              console.log('All fonts loaded and verified for Safari');
            } catch (error) {
              console.warn('Font loading verification failed:', error);
            }
          }
        }

        setFontsLoaded(true);
      } catch (error) {
        console.error('Error loading fonts:', error);
        setFontsLoaded(true); // Set to true anyway to prevent infinite loading
      }
    };

    if (!loading && (content.hero_title_font || content.hero_subtitle_font || content.ticker_font_family || customFonts.length > 0)) {
      loadFonts();
    } else if (!loading) {
      setFontsLoaded(true);
    }
  }, [content, customFonts, loading]);

  const handleScrollToWork = () => {
    const projectsSection = document.querySelector('[data-section="projects"]');
    if (projectsSection) {
      projectsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleContactClick = () => {
    window.location.href = '/about';
  };

  const handleVideoError = () => {
    setVideoError(true);
  };

  const openVideoModal = () => {
    setShowVideoModal(true);
  };

  const closeVideoModal = () => {
    setShowVideoModal(false);
  };

  // Function to check if URL is a Vimeo embed URL
  const isVimeoEmbed = (url: string) => {
    return url.includes('player.vimeo.com');
  };

  // Function to prepare Vimeo URL for autoplay
  const prepareVimeoUrl = (url: string) => {
    if (!isVimeoEmbed(url)) return url;

    // Add autoplay and mute parameters for Vimeo
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}autoplay=1&muted=1&loop=1&background=1&controls=0`;
  };

  // Function to prepare Vimeo URL for modal (with controls)
  const prepareVimeoUrlForModal = (url: string) => {
    if (!isVimeoEmbed(url)) return url;

    // Add autoplay but keep controls for modal
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}autoplay=1&controls=1`;
  };

  // New helper: video loaded callback
  const handleVideoLoaded = () => {
    setVideoLoaded(true);
  };

  // Get backup image URL - use admin specified image or default fallback
  const getBackupImageUrl = () => {
    if (content.hero_backup_image_url) {
      return content.hero_backup_image_url;
    }
    // Use a more neutral fallback image that matches your site's aesthetic
    return "https://readdy.ai/api/search-image?query=modern%20creative%20studio%20workspace%2C%20professional%20design%20environment%2C%20minimalist%20office%20space%2C%20contemporary%20architecture%2C%20clean%20aesthetic%2C%20natural%20lighting%2C%20creative%20workspace&width=1920&height=1080&seq=hero-backup&orientation=landscape";
  };

  // Enhanced font style with Safari fallbacks
  const getFontStyle = (fontName: string | undefined) => {
    if (!fontName || fontName === 'Archivo') {
      return { fontFamily: 'Archivo, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' };
    }

    // Check if it's a custom font
    const isCustomFont = customFonts.find(f => f.font_family === fontName);

    if (isCustomFont) {
      return {
        fontFamily: `"${fontName}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
      };
    }

    // Google Font with Safari fallbacks
    return {
      fontFamily: `"${fontName}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
    };
  };

  // Helper function to render text with line breaks
  const renderTextWithBreaks = (text: string) => {
    return text.split('<br>').map((line, index, array) => (
      <span key={index}>
        {line}
        {index < array.length - 1 && <br />}
      </span>
    ));
  };

  if (loading || !fontsLoaded) {
    return (
      <section className="relative h-screen flex items-center justify-center bg-black">
        <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
      </section>
    );
  }

  return (
    <>
      <section className="relative h-screen overflow-hidden">
        {/* Background Image (Always present as backup) */}
        <div className="absolute inset-0">
          <div
            className="w-full h-full bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url('${getBackupImageUrl()}')`,
              zIndex: 1,
            }}
          />
        </div>

        {/* Background Video (Overlays the image when loaded) */}
        {!videoError && content.hero_video_url && (
          <div className="absolute inset-0" style={{ zIndex: videoLoaded ? 2 : 0, opacity: videoLoaded ? 1 : 0 }}>
            {isVimeoEmbed(content.hero_video_url) ? (
              // Vimeo iframe embed
              <iframe
                src={prepareVimeoUrl(content.hero_video_url)}
                className="w-full h-full object-cover"
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: '100vw',
                  height: '56.25vw', // 16:9 aspect ratio
                  minHeight: '100vh',
                  minWidth: '177.77vh', // 16:9 aspect ratio
                  transform: 'translate(-50%, -50%)',
                }}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                onError={handleVideoError}
                onLoad={handleVideoLoaded}
              />
            ) : (
              // Regular video file
              <video
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                onError={handleVideoError}
                onLoadedData={handleVideoLoaded}
                onCanPlay={handleVideoLoaded}
                onLoadStart={() => {
                  // Force play on load for better browser compatibility
                  const video = document.querySelector('video');
                  if (video) {
                    video
                      .play()
                      .catch(() => {
                        console.log('Video autoplay failed, showing fallback image');
                        setVideoError(true);
                      });
                  }
                }}
              >
                <source src={content.hero_video_url} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        )}

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/40" style={{ zIndex: 3 }}></div>

        {/* Video Button - Bottom Right, next to arrow button */}
        <div className="absolute bottom-8 right-8" style={{ zIndex: 20 }}>
          <button
            className="w-12 h-12 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-full hover:bg-white/20 transition-all duration-300 cursor-pointer hover:scale-110"
            onClick={openVideoModal}
            aria-label="Watch video"
          >
            <i className="ri-fullscreen-line text-white text-xl"></i>
          </button>
        </div>

        {/* Content */}
        <div className="relative h-full flex items-center justify-center" style={{ zIndex: 10 }}>
          <div className="text-center text-white px-8 max-w-6xl mx-auto">
            <h1
              className={`${content.hero_title_size} font-black uppercase tracking-wide ${content.hero_title_spacing || defaultContent.hero_title_spacing} leading-none`}
              style={{ ...getFontStyle(content.hero_title_font), fontWeight: 900 }}
            >
              {content.hero_title_line1 && renderTextWithBreaks(content.hero_title_line1)}
              {content.hero_title_line2 && (
                <div className="block -mt-4">{renderTextWithBreaks(content.hero_title_line2)}</div>
              )}
            </h1>

            {content.hero_subtitle && (
              <p
                className="text-lg md:text-xl lg:text-2xl text-white/90 mb-0 -mt-2.5 max-w-4xl mx-auto leading-relaxed"
                style={{ ...getFontStyle(content.hero_subtitle_font), fontWeight: 400 }}
              >
                {renderTextWithBreaks(content.hero_subtitle || 'Crafting compelling visual narratives that connect brands with their audiences through innovative design and strategic storytelling.')}
              </p>
            )}

            <div className={`flex flex-col sm:flex-row gap-6 justify-center items-center ${content.hero_button_spacing || defaultContent.hero_button_spacing}`}>
              <button
                className="border-2 border-white text-white hover:bg-white hover:text-black px-6 py-3 text-base rounded-lg whitespace-nowrap transition-all duration-300 cursor-pointer"
                style={{ fontFamily: 'Archivo, sans-serif', fontWeight: 600 }}
                onClick={handleScrollToWork}
              >
                {content.hero_button1_text}
              </button>
              {(content.hero_reel_button_enabled === '1' || content.hero_reel_button_enabled === undefined) && (
                <button
                  className="border-2 border-white text-white hover:bg-white hover:text-black px-6 py-3 text-base rounded-lg whitespace-nowrap transition-all duration-300 cursor-pointer"
                  style={{ fontFamily: 'Archivo, sans-serif', fontWeight: 600 }}
                  onClick={openVideoModal}
                >
                  {content.hero_reel_button_text || defaultContent.hero_reel_button_text}
                </button>
              )}
            </div>

          </div>
        </div>

        {/* Scroll Indicator */}
        <div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white animate-bounce cursor-pointer hover:scale-110 transition-transform duration-300"
          onClick={handleScrollToWork}
          style={{ zIndex: 20 }}
        >
          <div className="w-12 h-12 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-full hover:bg-white/20 transition-colors">
            <i className="ri-arrow-down-line text-2xl"></i>
          </div>
        </div>

        {/* Video Modal */}
        {showVideoModal && (
          <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4" style={{ zIndex: 99999 }}>
            <div className="relative w-full max-w-6xl aspect-video">
              {/* Close Button */}
              <button
                onClick={closeVideoModal}
                className="absolute -top-12 right-0 w-12 h-12 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/50 transition-all duration-300 cursor-pointer z-10"
              >
                <i className="ri-close-line text-white text-2xl"></i>
              </button>

              {/* Video Content */}
              {(() => {
                // Use modal video URL if available, otherwise fall back to hero video URL
                const modalVideoUrl = content.hero_modal_video_url || content.hero_video_url || defaultContent.hero_video_url;

                if (isVimeoEmbed(modalVideoUrl)) {
                  return (
                    <iframe
                      src={prepareVimeoUrlForModal(modalVideoUrl)}
                      className="w-full h-full rounded-lg"
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                    />
                  );
                } else {
                  return (
                    <video className="w-full h-full rounded-lg" controls autoPlay muted>
                      <source src={modalVideoUrl} type="video/mp4" />
                    </video>
                  );
                }
              })()}
            </div>
          </div>
        )}
      </section>

      {/* Removed duplicate ticker tape section - it's already in ProjectsSection */}

      <style jsx>{`
        @keyframes scroll {
          0% {
            transform: translateX(100%);
          }
          100% {
            transform: translateX(-100%);
          }
        }
      `}</style>
    </>
  );
}
